/**
 * CertificateCatalogTab — Admin management of the global certificate catalog.
 * Allows admins to add, edit, toggle active status, and delete certificates.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '../../services/api';
import { getAdminToken } from './adminUtils';

const CATEGORY_COLORS = {
  'RED HAT': '#ee0000',
  'KUBERNETES': '#326ce5',
  'AWS': '#ff9900',
  'AZURE': '#0078d4',
  'GCP': '#4285f4',
  'DEVOPS': '#0db7ed',
  'LINUX': '#fcc624',
  'PROGRAMMING': '#6366f1',
  'ANSIBLE': '#e00',
  'DOCKER': '#2496ed',
};

const PRESET_CATEGORIES = Object.keys(CATEGORY_COLORS);

const EMPTY_FORM = {
  name: '',
  full_name: '',
  category: '',
  color: '#6366f1',
  is_active: true,
};

export const CertificateCatalogTab = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const adminToken = getAdminToken();

  const fetchCerts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.adminGetCertCatalog(adminToken);
      setCerts(data);
    } catch (err) {
      toast.error('Failed to load certificate catalog');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { fetchCerts(); }, [fetchCerts]);

  const handleEdit = (cert) => {
    setEditingId(cert.id);
    setForm({ name: cert.name, full_name: cert.full_name, category: cert.category, color: cert.color || '#6366f1', is_active: cert.is_active });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.full_name || !form.category) { toast.error('Name, full name, and category are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await apiService.adminUpdateCert(editingId, form, adminToken);
        toast.success('Certificate updated');
      } else {
        await apiService.adminCreateCert(form, adminToken);
        toast.success('Certificate added to catalog');
      }
      handleCancel();
      fetchCerts();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm(`Delete "${cert.name}" from catalog? This cannot be undone.`)) return;
    try {
      await apiService.adminDeleteCert(cert.id, adminToken);
      toast.success('Certificate deleted');
      fetchCerts();
    } catch (err) {
      toast.error(err?.detail || 'Failed to delete certificate');
    }
  };

  const categories = [...new Set(certs.map((c) => c.category))].sort();
  const filtered = certs.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || c.full_name.toLowerCase().includes(q);
    const matchCat = !filterCat || c.category === filterCat;
    return matchSearch && matchCat;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Certificate Catalog</h2>
          <p className="text-sm text-slate-400 mt-0.5">{certs.length} certificates · {certs.filter((c) => c.is_active).length} active</p>
        </div>
        <button onClick={handleNew} className="bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          + Add Certificate
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
          <h3 className="text-base font-semibold text-white mb-5">{editingId ? 'Edit Certificate' : 'Add Certificate'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Short Name * <span className="text-slate-600">(will be uppercased)</span></label>
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))} required maxLength={50} placeholder="e.g. RHCSA"
                  className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Category *</label>
                <select value={form.category} onChange={(e) => { setForm((f) => ({ ...f, category: e.target.value, color: CATEGORY_COLORS[e.target.value] || f.color })); }} required
                  className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50">
                  <option value="">Select category…</option>
                  {PRESET_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Full Name *</label>
              <input type="text" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} required maxLength={200} placeholder="e.g. Red Hat Certified System Administrator"
                className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
            </div>
            <div className="flex items-center gap-6">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Badge Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer border border-slate-600" />
                  <span className="text-sm text-slate-400">{form.color}</span>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: form.color }}>
                    {form.name?.[0] || '?'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button type="button" onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-slate-400">{form.is_active ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={handleCancel} className="text-slate-400 hover:text-white text-sm font-medium px-4 py-2 rounded-lg border border-slate-600 transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                {saving ? 'Saving…' : editingId ? 'Update' : 'Add Certificate'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search certificates…"
          className="flex-1 bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
          className="bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none">
          <option value="">All categories</option>
          {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-slate-500 text-sm text-center py-8">No certificates found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-700/50">
                <th className="text-left py-3 px-2">Badge</th>
                <th className="text-left py-3 px-2">Name</th>
                <th className="text-left py-3 px-2">Full Name</th>
                <th className="text-left py-3 px-2">Category</th>
                <th className="text-left py-3 px-2">Status</th>
                <th className="text-right py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.map((cert) => (
                <tr key={cert.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="py-3 px-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: cert.color || '#6366f1' }}>
                      {cert.name?.slice(0, 3) || '?'}
                    </div>
                  </td>
                  <td className="py-3 px-2 font-semibold text-white">{cert.name}</td>
                  <td className="py-3 px-2 text-slate-300 max-w-xs truncate">{cert.full_name}</td>
                  <td className="py-3 px-2">
                    <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">{cert.category}</span>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${cert.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>
                      {cert.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(cert)} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">Edit</button>
                      <button onClick={() => handleDelete(cert)} className="text-xs text-red-400 hover:text-red-300 transition-colors">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
